import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";

import { useDisclosure } from "@chakra-ui/react";

import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { IBankStatementDocument } from "@/jenesys-types/jenesys-entities/config";

import {
  downloadCSVFiles,
  downloadPDFFiles,
  loadStatementsStart,
  uploadBankStatements
} from "@/store/features/clientSettings/bankStatementDocuments/bankStatementDocuments";

interface BankStatementDocumentsProps {
  clientId: string | undefined;
}

export function useBankStatementDocuments({
  clientId
}: BankStatementDocumentsProps) {
  const dispatch = useDispatch();

  const [selectedStatements, setSelectedStatements] = useState<
    IBankStatementDocument[]
  >([]);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [files, setFiles] = useState<Array<{ id: string; file: File }>>([]);
  const uploadPDFDisclosure = useDisclosure();
  const downloadCSVsDisclosure = useDisclosure();

  const MAX_FILES = 10;
  const hasExceededLimit = files.length >= MAX_FILES;

  const {
    statements = [],
    isFetchingStatements,
    isUploading,
    isDownloadingCSV,
    isDownloadingPDF
  } = useSelector((state: any) => state.features.BankStatementDocuments);

  useEffect(() => {
    if (clientId) {
      dispatch(loadStatementsStart({ clientId, search: searchQuery }));
    }
  }, [dispatch, clientId, searchQuery, page]);

  const debouncedOnChange = useCallback((value: string) => {
    const debounced = debounce((val: string) => {
      setSearchQuery(val);
    }, 500);
    debounced(value);
  }, []);

  const handleSubmitFiles = (event: React.FormEvent) => {
    event.preventDefault();
    const fileObjects = files.map(({ file }) => file);
    dispatch(uploadBankStatements({ files: fileObjects }));
    setFiles([]);
    uploadPDFDisclosure.onClose();
  };

  const handleUploadStatements = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      const uploadedFiles = Array.from(event.target.files).map((file) => ({
        id: crypto.randomUUID(),
        file
      }));
      setFiles((prevFiles) =>
        [...prevFiles, ...uploadedFiles].slice(0, MAX_FILES)
      );
    }
  };

  const handleDownloadPDF = async (
    statementsToDownload: IBankStatementDocument[]
  ) => {
    dispatch(downloadPDFFiles({ statements: statementsToDownload }));
  };

  const handleDownloadCSV = async (
    statementsToDownload: IBankStatementDocument[]
  ) => {
    dispatch(downloadCSVFiles({ statements: statementsToDownload }));
    downloadCSVsDisclosure.onClose();
  };

  const handlePageClick = useCallback((event: { selected: number }) => {
    setPage(event.selected);
  }, []);

  const handleToggleSelectAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.checked) {
        setSelectedStatements([]);
        return;
      }
      setSelectedStatements(statements);
    },
    [statements]
  );

  const handleToggleSelection = useCallback(
    (
      event: ChangeEvent<HTMLInputElement>,
      statement: IBankStatementDocument
    ) => {
      if (!event.target.checked) {
        setSelectedStatements((prev) =>
          prev.filter(({ id }) => id !== statement.id)
        );
        return;
      }
      setSelectedStatements((prev) => [...prev, statement]);
    },
    []
  );

  const selectionsAreDownloadable = useMemo(
    () => selectedStatements.some((statement) => statement.download_csv_url),
    [selectedStatements]
  );

  return {
    statements,
    selectedStatements,
    page,
    searchQuery,
    files,
    setFiles,
    isUploading,
    isDownloadingCSV,
    isDownloadingPDF,
    isFetchingStatements,
    hasExceededLimit,
    uploadPDFDisclosure,
    downloadCSVsDisclosure,

    selectionsAreDownloadable,

    debouncedOnChange,
    handleSubmitFiles,
    handleUploadStatements,
    handleDownloadPDF,
    handleDownloadCSV,
    handleToggleSelection,
    handlePageClick,
    handleToggleSelectAll,
    setSelectedStatements
  };
}
